import { useFormikContext } from 'formik';
import { useContext, useEffect, useMemo, useState } from 'react';
import { upperCase, find } from 'lodash-es';
import useSmartField from 'hooks/useSmartField';
import { useTranslate, TFnType } from '@tolgee/react';
import { ClassActiveKnowledgeIdContext, CurrentKnowledgeValueContext } from './context';
import { useGetSegmentsValue } from '@knowledge/common/share';
import { Descendant, Text } from 'slate';
import { CustomSlateNodeType } from '../../../../components/TemplateEditor/types';
import { defaultDescendants } from '@knowledge/common/components/UploadEditor/utils';

export const transformItemV2Spu2KnowledgeSpu = (item: ItemSelectV2.Item): Knowledge.SPU.Spu => {
  const {
    skuInfo = [],
    internalItemId,
    itemUrl,
    imageUrl,
    platform,
    region,
    externalShopId,
    externalItemId,
    itemName,
  } = item;

  return {
    imageUrl: imageUrl?.[0],
    itemInternalId: internalItemId,
    itemUrl,
    platform,
    region,
    shopId: externalShopId,
    title: itemName,
    spuId: externalItemId,
    skus: skuInfo.map((sku) => ({
      skuId: sku.skuId,
      skuName: sku.skuName,
      sellerSku: sku.sellerSku,
    })),
  };
};

export const useValueName = () => {
  const { t } = useTranslate();
  const { active } = useContext(CurrentKnowledgeValueContext);
  const { field } = useSmartField<Knowledge.SPU.KnowledgeValue[]>(`values`);
  const activeField = find(field.value, { id: active });
  const { getSegmentsValue } = useGetSegmentsValue();
  return useMemo(() => {
    const segments = getSegmentsValue(activeField || {});
    return segments2text(t, segments);
  }, [activeField, t, getSegmentsValue]);
};

export const segments2text = (t: TFnType, segments: Knowledge.SPU.Segment[] = []) => {
  return segments.reduce((res, { type, content }) => {
    if (type === 'TEXT') {
      return res + content;
    }
    if (type === 'IMAGE') {
      return res + `[${t('phrase.图片')}]`;
    }
    if (type === 'VIDEO') {
      return res + `[${t('phrase.视频')}]`;
    }
    return res;
  }, '');
};

export const useIsEmptyValues = () => {
  const { values } = useFormikContext<Knowledge.SingleClass.Knowledge>();
  return values?.values?.length === 0;
};

export const getMultiLangValue = (source: string, lang: string, fallback: string) => {
  if (lang === 'zh-CN') lang = 'cn';
  if (lang === 'en-US') lang = 'en';
  if (lang === 'vi') lang = 'vn';
  if (lang === 'tl') lang = 'ph';
  try {
    const parsed = JSON.parse(source);
    // 区分useKnowledgeName 里的判断逻辑，那里需要优先展示fallback
    // 这里单品名称不可编辑，应该优先展示多语言
    return parsed[upperCase(lang)] || fallback;
  } catch (error) {
    return fallback;
  }
};

/**
 * 考虑到spuselect 渲染性能消耗大，这里当knowledgeId被激活的时候才去渲染
 */
export const useDeferredRender = () => {
  const { activeId } = useContext(ClassActiveKnowledgeIdContext);
  const { initialValues: values } = useFormikContext();
  const [rendered, setRendered] = useState(false);
  useEffect(() => {
    if (activeId === values.id) {
      setRendered(true);
    }
  }, [activeId, values, rendered]);

  return rendered;
};

// 高风险场景, 图片和使用使用的是CustomSlateNodeType.IMAGE
export function segments2descendants(segments: Knowledge.SPU.Segment[] = []) {
  const descendants = segments.reduce<Descendant[]>((res, { type, content }) => {
    if (type === 'TEXT') {
      const pre = res[res.length - 1];

      if (pre && pre.type === CustomSlateNodeType.PARAGRAPH) {
        const last = pre.children[pre.children.length - 1];

        if (Text.isText(last)) {
          last.text += content;

          return res;
        }
      }

      res.push({
        type: CustomSlateNodeType.PARAGRAPH,
        children: [
          {
            text: content,
          },
        ],
      });

      return res;
    }

    if (type === 'IMAGE') {
      res.push({
        type: CustomSlateNodeType.IMAGE,
        images: [{ imageId: content }],
        children: [{ text: 'image' }],
      });

      return res;
    }

    if (type === 'VIDEO') {
      res.push({
        type: CustomSlateNodeType.IMAGE,
        images: [{ imageId: content }],
        children: [{ text: 'video' }],
      });

      return res;
    }

    return res;
  }, []);

  return descendants.length > 0 ? descendants : defaultDescendants;
}

export function descendants2segments(descendants: Descendant[] = []) {
  return descendants
    .reduce<Knowledge.SPU.Segment[]>((res, cur) => {
      if (cur.type === CustomSlateNodeType.IMAGE) {
        if (cur.images[0].imageId) {
          res.push({
            type: cur.children[0].text === 'image' ? 'IMAGE' : 'VIDEO',
            content: cur.images[0].imageId,
          });
        }

        return res;
      }

      if (cur.type === CustomSlateNodeType.PARAGRAPH) {
        return cur.children.reduce((res, cur) => {
          if (!Text.isText(cur)) {
            return res;
          }

          const pre = res[res.length - 1];

          if (pre?.type === 'TEXT') {
            pre.content += cur.text;
          } else {
            res.push({
              type: 'TEXT',
              content: cur.text,
            });
          }

          return res;
        }, res);
      }

      return res;
    }, [])
    .filter(({ type, content }) => type !== 'TEXT' || content);
}
